import React, { useState, useContext, useCallback } from 'react'
import { AuthContext } from '@/layout/Auth'
import {
  TextField,
  TextFieldProps,
  Stack,
  Autocomplete,
  AutocompleteChangeReason,
  Avatar,
  FormControl,
  FormHelperText,
} from '@mui/material'
import { FormCard, ListWrapper, ScrollY, Row } from '../components'
import { InvitationInfo, RoleInfo, MemberInfo } from '@/types'
import Text from '@/components/Text'
import Button, { ConfirmButton } from '@/components/Button'
import Select from '@/components/Select'
import { Delete } from '@/icon'
import { primary } from '@/constant'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getRoles, inviteMembers, validateMember } from '@/api'
import { Message } from '@cx/ui'
import { ensureSecurityGate } from '@/utils/securityGate'

export const MemberSearchInput = ({
  value,
  onChange,
  error,
}: {
  value?: string
  onChange?: (value: string) => void
  error?: boolean
}) => {
  const emailTest = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [input, setInput] = useState<string>('')
  const [currentMember, setCurrentMember] = useState<MemberInfo>()
  const [validating, setValidating] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  useQuery(
    ['validateMember', input],
    () =>
      // @ts-ignore
      validateMember(orgInfo?.id, {
        mail: emailTest.test(input) ? input : null,
        user_id: emailTest.test(input) ? null : input,
      }),
    {
      enabled: validating && input?.length > 0,
      onSuccess: (res) => {
        setCurrentMember(res)
        setInput('')
        setErrorMessage('')
        onChange?.(res.id as string)
        setValidating(false)
      },
      onError: (err: string) => {
        setErrorMessage(err)
        setValidating(false)
      },
    },
  )
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event?.target?.value
    setInput(inputValue ?? '')
  }, [])

  const handleKeyUp = useCallback((event: React.KeyboardEvent) => {
    const code = event?.code
    if (code === 'Enter') {
      setValidating(true)
    }
  }, [])

  const changeValidate = useCallback(
    (state: boolean) => () => {
      setValidating(state)
    },
    [],
  )

  const renderInput = useCallback(
    (params: TextFieldProps) => (
      <TextField
        {...params}
        InputProps={{
          ...params.InputProps,
        }}
        label='用户ID/邮箱'
        required={true}
        error={error}
        onBlur={changeValidate(true)}
        onKeyUp={handleKeyUp}
      />
    ),
    [changeValidate, error, handleKeyUp],
  )

  const onAutoCompleteChange = useCallback(
    (event: React.SyntheticEvent, value: string[], reason: AutocompleteChangeReason) => {
      if (reason === 'clear') {
        setInput('')
        onChange?.('')
        setErrorMessage('')
      }
    },
    [onChange],
  )

  return (
    <>
      <Autocomplete
        multiple
        freeSolo={true}
        options={[]}
        open={false}
        inputValue={input}
        renderInput={renderInput}
        // @ts-ignore
        onInputChange={handleInputChange}
        value={
          (value?.length || 0) > 0
            ? [
                currentMember?.id === value
                  ? (currentMember?.name?.length || 0) > 0
                    ? currentMember?.name
                    : currentMember?.nickname
                  : '',
              ]
            : []
        }
        // @ts-ignore
        onChange={onAutoCompleteChange}
        ChipProps={{
          onDelete: () => {},
          avatar: (
            <Avatar
              src={currentMember?.head_img_url}
              variant='circular'
              sx={{ backgroundColor: primary, color: '#fff !important' }}
            >
              {(currentMember?.name?.length || 0) > 0 ? currentMember?.name?.[0] : currentMember?.nickname?.[0] ?? ''}
            </Avatar>
          ),
          sx: { height: 'auto', backgroundColor: 'transparent' },
        }}
        limitTags={1}
      />
      <FormHelperText sx={{ marginLeft: 0 }} error={true}>
        {errorMessage}
      </FormHelperText>
    </>
  )
}

export interface InviteCardProps {
  value: InvitationInfo
  onChange: (info: InvitationInfo) => void
  roleOptions: RoleInfo[]
  validating?: boolean
  onDelete?: () => void
  onErrorChange?: (hasError: boolean) => void
}

export const InviteCard: React.FC<InviteCardProps> = (props) => {
  const { roleOptions, validating, onDelete, value, onChange, onErrorChange } = props

  const handleChangeMemberId = useCallback(
    (memberId: string) => {
      onChange({ ...value, user_id: memberId })
      if (memberId && (value?.role_ids?.length || 0) > 0) {
        onErrorChange?.(false)
      } else {
        onErrorChange?.(true)
      }
    },
    [onChange, onErrorChange, value],
  )

  const handleChangeRoleIds = useCallback(
    (roleIds: number[] | string[]) => {
      onChange({ ...value, role_ids: roleIds as number[] })
      if (value?.user_id && roleIds?.length > 0) {
        onErrorChange?.(false)
      } else {
        onErrorChange?.(true)
      }
    },
    [onChange, onErrorChange, value],
  )

  return (
    <FormCard sx={{ gap: '30px' }}>
      <FormControl sx={{ width: '100%' }}>
        <MemberSearchInput
          // @ts-ignore
          value={value?.user_id}
          onChange={handleChangeMemberId}
        />
      </FormControl>
      <FormControl sx={{ width: '100%' }}>
        <Select
          value={value?.role_ids ?? []}
          // @ts-ignore
          onChange={handleChangeRoleIds}
          label='角色'
          required={true}
          multiple={true}
          options={
            roleOptions?.map((option) => ({
              value: option.id,
              label: option.name,
            })) ?? []
          }
        />
        <FormHelperText sx={{ marginLeft: 0 }} error={true}>
          {validating && value?.role_ids?.length === 0 ? '请选择角色' : ''}
        </FormHelperText>
      </FormControl>
      <Delete sx={{ fontSize: '16px', color: primary, cursor: 'pointer' }} onClick={onDelete} />
    </FormCard>
  )
}

export interface InvitationItem {
  id: number
  hasError: boolean
  info: InvitationInfo
}

export interface InvitationListProps {
  onCancel: () => void
}

const InvitationList: React.FC<InvitationListProps> = (props) => {
  const { onCancel } = props
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [invitations, setInvitations] = useState<InvitationItem[]>([
    { id: Date.now(), hasError: true, info: { user_id: null, role_ids: [] } },
  ])
  const [validating, setValidation] = useState<boolean>(false)

  const { data: res } = useQuery(
    ['getRoles', orgInfo?.id],
    // @ts-ignore
    () => orgInfo?.id && getRoles(orgInfo?.id),
    {
      enabled: !!orgInfo?.id,
    },
  )

  const inviteMutation = useMutation(inviteMembers, {
    onSuccess: () => {
      setValidation(false)
      Message.success('邀请成功')
      onCancel()
    },
  })

  const handleInvite = useCallback(() => {
    ;(async () => {
      if (!(await ensureSecurityGate('admin_operation'))) return
      setValidation(true)
      const hasError = invitations.findIndex((item) => item.hasError) > -1
      if (!hasError) {
        const invitationsData = invitations?.map((item) => item.info)
        // @ts-ignore
        inviteMutation.mutate({ id: orgInfo?.id, users: invitationsData })
      }
    })()
  }, [invitations, inviteMutation, orgInfo?.id])

  const handleDelete = useCallback(
    (id: number) => () => {
      const updated = [...invitations].filter((item) => item.id !== id)
      setInvitations([...updated])
    },
    [invitations],
  )

  const handleAdd = useCallback(() => {
    const newItem = {
      id: Date.now(),
      hasError: true,
      info: { user_id: null, role_ids: [] as number[] },
    }
    const updated = [...invitations]
    updated.push(newItem!)
    setValidation(false)
    setInvitations([...updated])
  }, [invitations])

  const handleUpdate = (id: number, value: InvitationInfo) => {
    const updated = [...invitations]
    updated.find((item) => item.id === id)!.info = value
    setInvitations([...updated])
  }

  const handleErrorStatus = (id: number, hasError: boolean) => {
    const updated = [...invitations]
    updated.find((item) => item.id === id)!.hasError = hasError
    setInvitations([...updated])
  }

  return (
    <Stack sx={{ height: '100%', gap: '30px' }}>
      <Text weight='bold'>邀请用户</Text>
      <ScrollY sx={{ flex: 1 }}>
        <ListWrapper sx={{ gap: '30px' }}>
          {invitations?.map((item) => (
            <InviteCard
              key={`invitation-${item.id}`}
              validating={validating}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(value) => handleUpdate(item.id, value)}
              // eslint-disable-next-line react/jsx-no-bind
              onErrorChange={(hasError: boolean) => handleErrorStatus(item.id, hasError)}
              value={item.info}
              roleOptions={res?.data ?? []}
              onDelete={handleDelete(item.id)}
            />
          ))}
          <Button
            type='secondary'
            sx={{
              width: '100%',
              border: '1px dashed #d9d9d9',
              position: 'sticky',
              bottom: 0,
              background: '#fff',
            }}
            onClick={handleAdd}
          >
            添加用户
          </Button>
        </ListWrapper>
      </ScrollY>
      <Row sx={{ justifyContent: 'flex-end', gap: '16px' }}>
        <Button type='secondary' onClick={onCancel}>
          取消
        </Button>
        <ConfirmButton onClick={handleInvite}>确认邀请</ConfirmButton>
      </Row>
    </Stack>
  )
}

export default InvitationList
